import React, { useEffect, useState } from 'react';
import { Dialog } from '@mui/material';
import {
    Grid,
    Box,
    Typography
} from '@mui/material';
import { fetchEventTemplate, getEventDetails } from "src/services/EventService";
import { useDispatch } from 'react-redux';
import { AppActions } from 'src/store/AppReducer';
import SnackBar from 'src/components/SnackBar/SnackBar';
import { EventDetails } from './interface/EventDetails';
import { getAudiences } from 'src/services/EventService';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import PeopleAltOutlinedIcon from '@mui/icons-material/PeopleAltOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import CloseIcon from '@mui/icons-material/Close';
import InsertPhotoIcon from '@mui/icons-material/InsertPhoto';
import { Margin } from '@mui/icons-material';


const PreviewToggleModel = ({ previewOpen, previewToggle,eventId }) => {
    const dispatch = useDispatch();
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snackbarSeverity, setSnackbarSeverity] = useState('');
    const [snackBarMessage, setSnackBarMessage] = useState('');
    const [event,setEvent] = useState<EventDetails |null>();
    const [outlookEmployees, setOutlookEmployees] = useState([]);
    const [outlookGroups, setOutlookGroups] = useState([]);
    const [audience, setAudience] = useState([]);
    const [optionalAudience, setOptionalAudience] = useState([]);
    const [base64Image, setBase64Image] = useState("");

    const GridItemStyle = {
        display:"flex",
        marginBottom:"10px"
    };

    const IconStyle = {
        marginRight:"10px"
    };

    const formatTo12Hour = (time: String) => {
        const [hours, minutes] = time.split(":");
        const date = new Date();
        date.setHours(Number(hours), Number(minutes));

        const options: Intl.DateTimeFormatOptions = {
            hour: "2-digit",
            minute: "2-digit",
            hour12: true,
        };

        return date.toLocaleTimeString("en-US", options);
    };

    const loadEventData = async () => {
        try {
            dispatch(AppActions.showLoader());

            const [eventRes, audienceRes] = await Promise.all([
                getEventDetails(eventId,true),
                getAudiences()
            ]);

            if (eventRes?.data?.statusCode === 200) {
                const eventData = eventRes.data.data;
                setEvent(eventData);

                const { users = [], groups = [] } = audienceRes?.data?.data || {};
                setOutlookEmployees(users);
                setOutlookGroups(groups);

                const invitedEmployeeNames = users
                    .filter(user => eventData?.inviteUserEmails.includes(user.email))
                    .map(user => user.displayName);

                const invitedGroupNames = groups
                    .filter(group => eventData?.groupIds.includes(group.id))
                    .map(group => group.displayName);

                const optionalEmployeeNames = users
                    .filter(user => eventData?.optionalInviteUserEmails.includes(user.email))
                    .map(user => user.displayName);

                const optionalGroupNames = groups
                    .filter(group => eventData?.optionalGroupIds.includes(group.id))
                    .map(group => group.displayName);

                setAudience([...invitedEmployeeNames, ...invitedGroupNames]);
                setOptionalAudience([...optionalEmployeeNames, ...optionalGroupNames]);
                if(eventData.templateName!=null){
                    getEventTemplate(eventData);
                }
            }
            
        } catch (error) {
            setSnackBarMessage("Could not load event details. Please try again later.");
            setSnackbarSeverity("error");
            setSnackBarOpen(true);
        } finally {
            dispatch(AppActions.hideLoader());
        }
    };

    const getEventTemplate = (eventDetails:EventDetails) => {

        dispatch(AppActions.showLoader());
        
        const body = {
            title: eventDetails.eventName,
            subtitle: eventDetails.subtitle,
            date: eventDetails.date ,
            startTime: eventDetails.timeFrom,
            endTime: eventDetails.timeTo,
            location: eventDetails.venue,
            facilitator: eventDetails.facilitator,
            audience: eventDetails.audience,
            templateName: eventDetails.templateName
        };
        
        fetchEventTemplate(body)
            .then(async (response) => {
                setBase64Image(response.data.data);
                dispatch(AppActions.hideLoader());
            })
            .catch((error) => {
                dispatch(AppActions.hideLoader());
                setSnackBarMessage('Unable to get event template');
                setSnackbarSeverity('error');
                setSnackBarOpen(true);
            });
    };

    useEffect(() => {
        if(previewOpen){
           loadEventData();
        }else{
            setEvent(null);
            setAudience(null);
            setOptionalAudience(null);
            setBase64Image("");
        }
    }, [previewOpen]);

    const handleCloseSnackbar = (open: boolean) => {
        setSnackBarOpen(open);
        if (!open) {
            previewToggle();
        }
    };

    return (
        <>
            <Dialog
                open={previewOpen}
                keepMounted
                maxWidth="sm"
                fullWidth
                scroll="paper"
                onClose={previewToggle}
                >
                    <Grid container direction="column" sx={{ p: 3}} >
                        <CloseIcon onClick={previewToggle} sx={{ position: 'absolute',top: '10px',right: '20px',cursor: 'pointer',fontSize: '20px'}}/>
                        <Grid style={GridItemStyle}>
                            <Typography fontSize={25} fontWeight={600} maxWidth={550} sx={{ overflowWrap: 'break-word',wordBreak: 'break-word',whiteSpace: 'normal'}}>
                                {event?.eventName}
                            </Typography>
                        </Grid>
                        <Grid style={GridItemStyle}>
                            <Typography mb={2} fontSize={22} fontWeight={500} maxWidth={550} sx={{ overflowWrap: 'break-word',wordBreak: 'break-word',whiteSpace: 'normal'}}>
                                {event?.subtitle}
                            </Typography>
                        </Grid>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Grid item style={GridItemStyle}>
                                <CalendarTodayIcon style={IconStyle} />
                                <Typography>
                                    <strong>Date :</strong> {event?.date}
                                </Typography>
                            </Grid>
                            <Grid item style={GridItemStyle} gap={1}>
                                <AccessTimeIcon style={{...IconStyle,marginRight:0}} />
                                <Typography>
                                    <strong>From :</strong> {event?.timeFrom ? formatTo12Hour(event?.timeFrom) : ""}
                                </Typography>
                                <Typography>
                                    <strong>To :</strong> {event?.timeTo ? formatTo12Hour(event?.timeTo) : ""}
                                </Typography>
                            </Grid>
                        </Grid>
                        <Grid style={GridItemStyle}>
                            <LocationOnOutlinedIcon style={IconStyle} />
                            <Typography>
                                <strong>Location :</strong> {event?.venue}
                            </Typography>
                        </Grid>
                        <Grid style={GridItemStyle}>
                            <PersonOutlinedIcon style={IconStyle}/>
                            <Typography>
                                <strong>Facilitator :</strong> {event?.facilitator ? event?.facilitator : "N/A"}
                            </Typography>
                        </Grid>
                        <Grid style={GridItemStyle}>
                            <PeopleAltOutlinedIcon style={IconStyle}/>
                            <Typography>
                                <strong>Attendees :</strong> {audience?.length ? audience.join(",") : ""}
                            </Typography>
                        </Grid>
                        <Grid style={GridItemStyle}>
                            <PeopleAltOutlinedIcon style={IconStyle}/>
                            <Typography>
                               <strong>Optional Attendees :</strong> {optionalAudience?.length ? optionalAudience.join(",") : "N/A"}
                            </Typography>
                        </Grid>
                        <Grid style={{ ...GridItemStyle, width: '94%' }}>
                            <DescriptionOutlinedIcon style={IconStyle} />
                            <div style={{ width: '100%' }}>
                                <Typography sx={{ fontWeight: 'bold', mb: 1 }}>
                                    Description:
                                </Typography>
                                <div
                                    style={{ overflowWrap: 'break-word', wordBreak: 'break-word' }}
                                    dangerouslySetInnerHTML={{ __html: event?.description?.toString() }}
                                />
                            </div>
                        </Grid>
                        {base64Image && base64Image !== '' && (
                            <Grid container alignItems="center" spacing={1} sx={{ ...GridItemStyle, display: 'flex',marginLeft:'1px'}}>
                                <InsertPhotoIcon style={{...IconStyle}}/>
                                <Typography>
                                    Template 
                                </Typography>
                                <Box 
                                    component="img" src={ `data:image/png;base64,${base64Image}`}             
                                    alt="selectedTemplate Image"
                                    sx={{width: "100%",height: "100%",objectFit: "cover",display: "block",marginTop:"1%"}}
                                />
                            </Grid>
                        )}
                    </Grid>
                
            </Dialog>
            <SnackBar
                snackBarOpen={snackBarOpen}
                setSnackBarOpen={handleCloseSnackbar}
                snackBarMessage={snackBarMessage}
                snackbarSeverity={snackbarSeverity}
            />
        </> 
    );
};

export default PreviewToggleModel;

